import React from 'react'
import { CartesianGrid, DotProps, Line, LineChart, ResponsiveContainer, Tooltip, TooltipProps } from 'recharts'
import { AreaDot, ChartValues, PowerPrice } from 'src/Libs/dynamic-power-prices/types'
import { POWER_PRICE_COLOR } from '../consts'
import { ValueType } from 'recharts/types/component/DefaultTooltipContent'
import YAxisPowerPriceChart from './YAxisPowerPriceChart'
import XAxisPowerPriceChart from './XAxisPowerPriceChart'
import { AxisDomain } from 'recharts/types/util/types'

interface Props {
  chartData: ChartValues[]
  isModal: boolean
  handleOnClick: () => void
  handleMouseLeave: () => void
  getTooltipValues?: (props: TooltipProps<ValueType, keyof PowerPrice>) => JSX.Element
  renderActiveDot: (d: AreaDot, color: string) => JSX.Element
  renderStaticDot: (d: AreaDot, color: string) => JSX.Element
  yAxisDomain: AxisDomain
  minChartHeight?: string
  classChartContainer?: string
}

/**
 * Shows the power price as line chart if AI360v2 is enabled.
 */
const PowerPriceChartAI360: React.FC<Props> = ({
  chartData,
  isModal,
  handleOnClick,
  handleMouseLeave,
  getTooltipValues,
  renderActiveDot,
  renderStaticDot,
  yAxisDomain,
  minChartHeight,
  classChartContainer,
}) => {
  const lastChartDataIndex = chartData.length - 1
  const yAxis = YAxisPowerPriceChart({ isModal: !!isModal, yAxisDomain })
  const xAxis = XAxisPowerPriceChart({ isModal: !!isModal, lastItemIndex: lastChartDataIndex })

  return (
    <ResponsiveContainer minHeight={minChartHeight} height="100%" className={classChartContainer}>
      <LineChart
        data={chartData}
        style={{ position: 'relative', cursor: 'inherit' }}
        margin={{
          top: isModal ? 30 : 3,
          right: 0,
          left: 0,
          bottom: 0,
        }}
        onClick={handleOnClick}
        onMouseEnter={handleOnClick}
        // recharts calls onMouseDown on touchstart anywhere in the chart, so a drag without a prior tap also enables the hover dot
        onMouseDown={handleOnClick}
        onMouseLeave={handleMouseLeave}
      >
        {isModal && <CartesianGrid vertical={false} />}

        <Line
          type="stepAfter"
          dataKey="price"
          strokeLinecap="round"
          strokeLinejoin="round"
          stroke={POWER_PRICE_COLOR}
          strokeWidth={2}
          activeDot={(d: DotProps) => renderActiveDot(d as AreaDot, POWER_PRICE_COLOR)}
          dot={(d: AreaDot) => renderStaticDot(d, POWER_PRICE_COLOR)}
          isAnimationActive={false}
          onClick={handleOnClick}
          onMouseEnter={handleOnClick}
          onTouchStart={handleOnClick}
          onTouchEnd={handleMouseLeave}
        />

        {yAxis}
        {xAxis}

        {isModal && <Tooltip cursor={false} content={getTooltipValues} />}
      </LineChart>
    </ResponsiveContainer>
  )
}

export default PowerPriceChartAI360
